/**
 * Ctrl+K (⌘K on a Mac): go anywhere and do the everyday things without the mouse
 * (docs/refactor/07-ctrl-k.md). The same palette in the browser and in the desktop app; the desktop
 * also finds the active project's files and opens them in the Editor.
 *
 * What it lists comes from the places that already decide it: the screens from the navigation tree
 * (so nobody is offered a screen they may not open), the projects from the active repository, the
 * documents, sessions and tasks from the API. Nothing is fetched until it opens.
 */

import { openNewWindow } from '../lib/appWindows'
import { isFused, toggleFused } from '../lib/fuse'
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { api } from '../lib/api'
import { clearSession, getSession } from '../lib/auth'
import { useI18n, LOCALES, LOCALE_LABELS } from '../lib/i18n'
import { useTheme } from '../lib/theme'
import { openSuperSkills, redock, sendCommand } from '../lib/dockPlacement'
import { local as localized } from '../lib/superSkills'
import { repoBranches, repoCheckout } from '../lib/desktop'
import { folderFor } from '../lib/repoFolders'
import { notify } from '../lib/inboxNotify'
import { SLASH_COMMANDS } from '../lib/composer'
import { visibleAreas } from '../lib/navigation'
import { useActiveRepository } from '../lib/activeRepository'
import { useRepoFiles } from '../lib/repoSource'
import { isDesktop, repoRead, repoWrite } from '../lib/desktop'
import { requestOpenInEditor } from '../lib/openProjects'
import { followRepository } from '../lib/followProject'
import { useSuperSkills } from '../lib/teamSuperSkills'
import { useDebounced } from '../lib/useDebounced'
import { formatRelative } from '../lib/format'
import {
  PALETTE_EVENT,
  isPaletteShortcut,
  rankEntries,
  recentIds,
  rememberRecent,
  type PaletteEntry,
} from '../lib/palette'

/** Mounted once, in the Shell: listens for Ctrl+K and the header's button, draws the palette while open. */
export function CommandPalette() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!isPaletteShortcut(event)) return
      // Before the browser (its own Ctrl+K) and before a text box that would type it.
      event.preventDefault()
      event.stopPropagation()
      setOpen((current) => !current)
    }
    const onOpen = () => setOpen(true)
    window.addEventListener('keydown', onKey, true)
    window.addEventListener(PALETTE_EVENT, onOpen)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener(PALETTE_EVENT, onOpen)
    }
  }, [])

  const close = useCallback(() => setOpen(false), [])
  return open ? <PaletteDialog onClose={close} /> : null
}

function PaletteDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n()
  const listId = useId()
  const input = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [problem, setProblem] = useState<string | null>(null)
  const [recent] = useState(recentIds)

  const entries = usePaletteEntries(query)
  const groups = useMemo(() => rankEntries(entries, query, recent), [entries, query, recent])
  const flat = useMemo(() => groups.flatMap((group) => group.entries), [groups])

  // Focus goes into the box, and back to where it was when the palette closes.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null
    input.current?.focus()
    return () => before?.focus?.()
  }, [])

  // A new text starts again at the best match.
  useEffect(() => setActive(0), [query])

  const optionId = (index: number) => `${listId}-option-${index}`
  useEffect(() => {
    document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' })
    // optionId only depends on listId, which never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  // Closes at once; an entry that has to fetch first (a file) closes when it is done, or says it failed.
  const choose = (entry: PaletteEntry | undefined) => {
    if (!entry) return
    setProblem(null)
    // Signing out is not something to offer again first.
    if (entry.id !== 'action:sign-out') rememberRecent(entry.id)
    const failed = () => setProblem(t('palette.failed'))
    try {
      const result = entry.run()
      if (result instanceof Promise) result.then(onClose, failed)
      else onClose()
    } catch {
      failed()
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((current) => (flat.length ? (current + 1) % flat.length : 0))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((current) => (flat.length ? (current - 1 + flat.length) % flat.length : 0))
    } else if (event.key === 'Home' && flat.length) {
      event.preventDefault()
      setActive(0)
    } else if (event.key === 'End' && flat.length) {
      event.preventDefault()
      setActive(flat.length - 1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      choose(flat[active])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    } else if (event.key === 'Tab') {
      // The box is the only stop: the list is walked with the arrows.
      event.preventDefault()
    }
  }

  let index = -1
  return (
    <div
      className="palette__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="palette" role="dialog" aria-modal="true" aria-label={t('palette.title')}>
        <div className="palette__search">
          <Search size={16} aria-hidden />
          <input
            ref={input}
            className="palette__input"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={flat.length ? optionId(active) : undefined}
            aria-label={t('palette.title')}
            placeholder={t('palette.placeholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="palette__kbd">Esc</kbd>
        </div>

        <div className="palette__list" id={listId} role="listbox" aria-label={t('palette.results')}>
          {groups.map((group) => (
            <div key={group.section} role="group" aria-label={t(`palette.section.${group.section}`)} className="palette__group">
              <div className="palette__section tiny muted" aria-hidden>{t(`palette.section.${group.section}`)}</div>
              {group.entries.map((entry) => {
                index += 1
                const mine = index
                return (
                  <div
                    key={`${group.section}:${entry.id}`}
                    id={optionId(mine)}
                    role="option"
                    aria-selected={mine === active}
                    className={`palette__option${mine === active ? ' is-active' : ''}`}
                    onMouseMove={() => mine !== active && setActive(mine)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(entry)}
                  >
                    <span className="palette__label truncate">{entry.label}</span>
                    {entry.hint && <span className="palette__hint tiny muted truncate">{entry.hint}</span>}
                  </div>
                )
              })}
            </div>
          ))}
          {flat.length === 0 && <div className="palette__empty small muted">{t('palette.empty')}</div>}
        </div>

        <div className="palette__foot tiny muted">
          {problem ? <span className="palette__problem" role="alert">{problem}</span> : <span>{t('palette.keys')}</span>}
        </div>
      </div>
    </div>
  )
}

/** Every entry the palette can offer this person now, from each provider. */
function usePaletteEntries(query: string): PaletteEntry[] {
  const { t, locale, setLocale } = useI18n()
  const [theme, setTheme] = useTheme()
  const navigate = useNavigate()
  const session = getSession()
  const typed = useDebounced(query.trim(), 200)

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const { linked, active, choose } = useActiveRepository()
  const { data: tree } = useQuery({ queryKey: ['docs', 'tree'], queryFn: api.docs.tree, staleTime: 60_000, retry: false })
  // The super skills (docs/refactor/50), each one straight from the keyboard; never in Kids mode.
  // The catalog's super skills and the team's own (docs/refactor/51, §6.2).
  const { skills: superSkillList } = useSuperSkills(me !== undefined && !me?.kidsMode)
  const { data: docHits } = useQuery({
    queryKey: ['docs', 'search', typed],
    queryFn: () => api.docs.search(typed),
    enabled: typed.length >= 3,
    retry: false,
  })
  const { data: sessions } = useQuery({
    queryKey: ['palette', 'sessions'],
    queryFn: () => api.sessions.list({ pageSize: 20 }),
    staleTime: 30_000,
    retry: false,
  })
  const { data: tasks } = useQuery({
    queryKey: ['palette', 'tasks', typed],
    queryFn: () => api.tasks.list({ pageSize: 20, search: typed || undefined }),
    staleTime: 30_000,
    retry: false,
  })
  // The active project's files, read from this machine: the desktop only.
  const { data: repo } = useRepoFiles(isDesktop() ? active : null)
  // Its branches, to switch from here (desktop; fetched once something is typed).
  const queryClient = useQueryClient()
  const branchFolder = isDesktop() && active ? folderFor(active.name) : null
  const { data: branches } = useQuery({
    queryKey: ['palette-branches', branchFolder],
    queryFn: () => repoBranches(branchFolder!),
    enabled: Boolean(branchFolder && typed),
    staleTime: 15_000,
    retry: false,
  })

  return useMemo(() => {
    const entries: PaletteEntry[] = []
    const go = (to: string) => () => navigate(to)

    // Screens: the tree as this person sees it.
    const areas = visibleAreas({ role: session?.role, isDeveloper: Boolean(me?.developerId), bugHuntEnabled: me?.bugHuntEnabled !== false })
    for (const area of areas) {
      if (area.to) entries.push({ id: `go:${area.to}`, section: 'go', label: t(area.labelKey), keywords: area.id, run: go(area.to) })
      for (const item of area.items) {
        entries.push({ id: `go:${item.to}`, section: 'go', label: t(item.labelKey), hint: t(area.labelKey), keywords: item.id, run: go(item.to) })
      }
    }
    entries.push({ id: 'go:/settings/account', section: 'go', label: t('settings.account'), hint: t('nav.settings'), run: go('/settings/account') })

    // Projects: choosing one makes it the active project (Play, Audit, the Explorer, the prompt box).
    for (const repository of linked) {
      const isActive = repository.id === active?.id
      entries.push({
        id: `project:${repository.id}`,
        section: 'projects',
        label: repository.name,
        hint: isActive ? t('palette.projectActive') : t('palette.projectUse'),
        keywords: repository.projectName ?? undefined,
        run: () => {
          choose(repository.id)
          void followRepository(repository, queryClient)
        },
      })
      // Several windows (lib/appWindows.ts): this project in a window of its own, on the desktop.
      // Only when the person asks for a window: otherwise these would crowd every search for a file.
      if (isDesktop() && typed && /jan|win|nov|new/i.test(typed))
        entries.push({
          id: `window:${repository.id}`,
          section: 'projects',
          label: t('palette.newWindowFor', { name: repository.name }),
          keywords: `${repository.name} janela window`,
          run: () => void openNewWindow(repository.id),
        })
    }

    // Files of the active project, on the desktop: opened in the Editor.
    if (repo?.live && repo.folder) {
      const folder = repo.folder
      for (const path of repo.files) {
        const name = path.split('/').pop() ?? path
        entries.push({
          id: `file:${active?.id ?? ''}:${path}`,
          section: 'files',
          label: name,
          hint: path,
          run: async () => {
            const content = await repoRead(folder, path)
            requestOpenInEditor({ name, content, path: `${folder}/${path}`, writer: (text) => repoWrite(folder, path, text) }, 'edit')
            navigate('/project/editor')
          },
        })
      }
    }

    // Documents: every one by its title and path; with three letters typed, also by what they say.
    const seenDocs = new Set<string>()
    for (const doc of tree?.docs ?? []) {
      seenDocs.add(doc.id)
      entries.push({ id: `doc:${doc.id}`, section: 'docs', label: doc.title || doc.name, hint: doc.path, run: go(`/project/docs?doc=${encodeURIComponent(doc.id)}`) })
    }
    if (typed.length >= 3) {
      for (const hit of docHits ?? []) {
        if (seenDocs.has(hit.id)) continue
        // Found by the server in the text: the words typed are what it matched.
        entries.push({ id: `doc:${hit.id}`, section: 'docs', label: hit.title, hint: hit.path, keywords: typed, run: go(`/project/docs?doc=${encodeURIComponent(hit.id)}`) })
      }
    }

    // Sessions: the latest, newest first.
    for (const s of sessions?.items ?? []) {
      const what = s.taskTitle ?? s.currentStepTitle ?? s.repositoryName ?? t('palette.session')
      entries.push({
        id: `session:${s.workSessionId}`,
        section: 'sessions',
        label: what,
        hint: [s.developerName, s.repositoryName, formatRelative(s.lastActivityAt, locale)].filter(Boolean).join(' · '),
        keywords: [s.taskExternalId, s.branch].filter(Boolean).join(' '),
        run: go(`/work/${s.workSessionId}`),
      })
    }

    // Tasks: searched by the server when something is typed.
    for (const task of tasks?.items ?? []) {
      entries.push({
        id: `task:${task.id}`,
        section: 'tasks',
        label: `${task.externalId} · ${task.title}`,
        hint: [task.projectName, t(`taskStatus.${task.status}`)].filter(Boolean).join(' · '),
        keywords: typed || undefined,
        run: go(`/project/tasks?search=${encodeURIComponent(task.externalId)}`),
      })
    }

    // Actions.
    entries.push({ id: 'action:prompt', section: 'actions', label: t('palette.action.prompt'), keywords: 'prompt', run: redock })
    // The free local AI's guided install (SDD ia-local-gratuita, task 8), one search away.
    entries.push({ id: 'action:local-ai', section: 'actions', label: t('palette.action.localAi'), keywords: 'ia local gratis free ollama opencode instalar install', run: go('/agents/connect?tab=desktop') })
    if (isDesktop()) entries.push({ id: 'action:new-window', section: 'actions', label: t('palette.action.newWindow'), keywords: 'janela window nova new', run: () => void openNewWindow(active?.id ?? null) })
    // "Fundir" (docs/refactor/62): the IDE becomes the prompt and its answer over Windows; and back.
    if (isDesktop()) entries.push({ id: 'action:fuse', section: 'actions', label: t(isFused() ? 'palette.action.restore' : 'palette.action.fuse'), keywords: 'fundir fuse restaurar restore janela pequena flutuante float windows', run: () => void toggleFused() })
    // The project's branches (desktop, once something is typed): "main", "trocar branch"…
    if (typed && branchFolder) {
      for (const branch of branches?.local ?? []) {
        if (branch === branches?.current) continue
        entries.push({
          id: `branch:${branch}`,
          section: 'actions',
          label: t('palette.action.branch', { branch }),
          hint: branches?.current ? t('palette.action.branchFrom', { branch: branches.current }) : undefined,
          keywords: `branch git checkout trocar mudar ${branch}`,
          run: async () => {
            try {
              await repoCheckout(branchFolder, branch)
              void queryClient.invalidateQueries({ queryKey: ['repo-branches'] })
              void queryClient.invalidateQueries({ queryKey: ['repo-files'] })
              void queryClient.invalidateQueries({ queryKey: ['palette-branches'] })
            } catch (error) {
              notify(t('palette.action.branchFailed', { branch }), String(error).replace(/^Error:\s*/, '').slice(0, 200))
            }
          },
        })
      }
    }
    // The prompt box's /commands, from the keyboard: sent through the box, where anything typed goes.
    for (const command of SLASH_COMMANDS) {
      entries.push({ id: `command:${command.name}`, section: 'actions', label: `/${command.name} — ${t(command.labelKey)}`, hint: t(command.hintKey), keywords: `comando command ${command.name}`, run: () => sendCommand(`/${command.name}`) })
    }
    if (!me?.kidsMode) {
      for (const skill of superSkillList) {
        entries.push({ id: `superskill:${skill.key}`, section: 'actions', label: t('superSkills.palette', { name: localized(skill.title, locale) }), hint: localized(skill.description, locale), keywords: `super skill ${skill.key} ${localized(skill.title, 'en-US')}`, run: () => openSuperSkills(skill.key) })
      }
    }
    entries.push({ id: 'action:new-project', section: 'actions', label: t('openProjects.newProject'), hint: t('openProjects.newProjectHint'), keywords: 'iniciar start new project ideia idea', run: () => navigate('/start-project') })
    entries.push({
      id: 'action:theme',
      section: 'actions',
      label: theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark'),
      keywords: 'theme tema',
      run: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    })
    for (const code of LOCALES) {
      if (code === locale) continue
      entries.push({ id: `action:locale:${code}`, section: 'actions', label: t('palette.action.language', { language: LOCALE_LABELS[code] }), keywords: 'language idioma', run: () => setLocale(code) })
    }
    entries.push({ id: 'action:sign-out', section: 'actions', label: t('nav.signOut'), keywords: 'logout sair', run: clearSession })
    return entries
  }, [t, locale, setLocale, theme, setTheme, navigate, session?.role, me, linked, active, choose, repo, tree, docHits, typed, sessions, tasks, branches, branchFolder, queryClient, superSkillList])
}
